import {
  type EmailTemplateRow,
} from "@nexys/user-management-client";
import { AlertCircle, Check, RotateCcw, Save } from "lucide-react";
import { useEffect, useState, type FormEvent } from "react";

import { useUmClient } from "../../context/auth-context.js";
import { useCall } from "../../hooks/use-call.js";
import { errorMessage } from "../../lib/error-message.js";
import { Alert, AlertDescription } from "../ui/alert.js";
import { Badge } from "../ui/badge.js";
import { Button } from "../ui/button.js";
import { Field } from "../ui/field.js";
import { Input } from "../ui/input.js";
import { Label } from "../ui/label.js";
import { Skeleton } from "../ui/skeleton.js";
import { Textarea } from "../ui/textarea.js";

const TEMPLATE_LABELS: Record<string, string> = {
  verification: "Verify email address",
  magic_link: "Magic sign-in link",
  password_reset: "Password reset",
  invitation: "Tenant invitation",
};

const SOURCE_LABELS: Record<EmailTemplateRow["source"], string> = {
  project: "customised",
  global: "inherited (global default)",
  builtin: "inherited (built-in)",
};

export interface ProjectEmailTemplatesProps {
  projectId: string;
  /** The product's default locale — the variant shown first. */
  defaultLocale: string;
  canManage?: boolean;
}

/**
 * Per-product editor for the transactional emails. Each template shows
 * the copy that will actually be sent plus where it comes from, so an
 * owner can override it or reset back to what it inherits.
 */
export const ProjectEmailTemplates = ({
  projectId,
  defaultLocale,
  canManage = false,
}: ProjectEmailTemplatesProps) => {
  const [locale, setLocale] = useState(defaultLocale);
  const { data, loading, error, refresh } = useCall("listProjectEmailTemplates", {
    projectId,
    locale,
  });

  // The product's default locale changed underneath us (settings saved).
  useEffect(() => setLocale(defaultLocale), [defaultLocale]);

  if (error) {
    return (
      <Alert variant="destructive">
        <AlertCircle />
        <AlertDescription>{errorMessage(error)}</AlertDescription>
      </Alert>
    );
  }

  return (
    <div className="grid gap-4">
      <LocalePicker
        value={locale}
        defaultLocale={defaultLocale}
        onChange={setLocale}
      />
      {loading ? (
        <>
          <Skeleton className="h-28 w-full" />
          <Skeleton className="h-28 w-full" />
        </>
      ) : (
        (data?.templates ?? []).map((t) => (
          <TemplateEditor
            // Remount when the stored copy changes underneath the form.
            key={`${t.templateKey}:${t.locale}:${t.updatedAt ?? "builtin"}`}
            projectId={projectId}
            locale={locale}
            template={t}
            canManage={canManage}
            onChanged={refresh}
          />
        ))
      )}
    </div>
  );
};

/**
 * Locale of the variant being edited. Free text rather than a fixed
 * list — the service accepts any BCP-47 tag, and a product may ship
 * copy in languages this console doesn't know about.
 */
const LocalePicker = ({
  value,
  defaultLocale,
  onChange,
}: {
  value: string;
  defaultLocale: string;
  onChange: (v: string) => void;
}) => {
  const [draft, setDraft] = useState(value);
  useEffect(() => setDraft(value), [value]);

  return (
    <form
      className="flex items-end gap-2"
      onSubmit={(e) => {
        e.preventDefault();
        const next = draft.trim().toLowerCase();
        if (next) onChange(next);
      }}
    >
      <Field
        label="Locale"
        htmlFor="email-template-locale"
        description={`Which language variant to edit. The product default is "${defaultLocale}".`}
      >
        <Input
          id="email-template-locale"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          placeholder={defaultLocale}
          className="w-[8rem] font-mono"
        />
      </Field>
      <Button
        type="submit"
        variant="outline"
        disabled={!draft.trim() || draft.trim().toLowerCase() === value}
      >
        Load
      </Button>
    </form>
  );
};

const TemplateEditor = ({
  projectId,
  locale,
  template,
  canManage,
  onChanged,
}: {
  projectId: string;
  locale: string;
  template: EmailTemplateRow;
  canManage: boolean;
  onChanged: () => void;
}) => {
  const client = useUmClient();
  const [subject, setSubject] = useState(template.subject);
  const [body, setBody] = useState(template.body);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const dirty =
    subject.trim() !== template.subject.trim() || body !== template.body;
  const overridden = template.source === "project";

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    setSaved(false);
    const res = await client.saveProjectEmailTemplate({
      projectId,
      templateKey: template.templateKey,
      subject: subject.trim(),
      body,
      locale,
    });
    setSaving(false);
    if (res.ok) {
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
      onChanged();
    } else {
      setError(errorMessage(res.error, "Couldn't save this template."));
    }
  };

  /** Drop the override so the template falls back to what it inherits. */
  const resetToInherited = async () => {
    setResetting(true);
    setError(null);
    const res = await client.deleteProjectEmailTemplate({
      projectId,
      templateKey: template.templateKey,
      locale,
    });
    setResetting(false);
    if (res.ok) {
      onChanged();
    } else {
      setError(errorMessage(res.error, "Couldn't reset this template."));
    }
  };

  return (
    <form
      onSubmit={submit}
      className="grid gap-3 rounded-lg border bg-card p-4"
      noValidate
    >
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="grid gap-0.5">
          <Label className="text-sm font-medium">
            {TEMPLATE_LABELS[template.templateKey] ?? template.templateKey}
          </Label>
          <code className="text-muted-foreground font-mono text-xs">
            {template.templateKey}
          </code>
        </div>
        <Badge variant={overridden ? "default" : "muted"}>
          {SOURCE_LABELS[template.source]}
        </Badge>
      </div>

      {error ? (
        <Alert variant="destructive">
          <AlertCircle />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      ) : null}

      <Field label="Subject" htmlFor={`subject-${template.templateKey}`}>
        <Input
          id={`subject-${template.templateKey}`}
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
          disabled={!canManage}
        />
      </Field>
      <Field
        label="Body"
        htmlFor={`body-${template.templateKey}`}
        description={`Plain text. Available placeholders: ${template.variables
          .map((v) => `{{${v}}}`)
          .join(", ")}`}
      >
        <Textarea
          id={`body-${template.templateKey}`}
          value={body}
          onChange={(e) => setBody(e.target.value)}
          rows={8}
          disabled={!canManage}
          className="font-mono text-xs"
        />
      </Field>

      {canManage ? (
        <div className="flex flex-wrap items-center gap-2">
          <Button type="submit" loading={saving} disabled={!dirty}>
            {saved ? <Check /> : <Save />}
            {saved ? "Saved" : "Save"}
          </Button>
          {overridden ? (
            <Button
              type="button"
              variant="ghost"
              loading={resetting}
              onClick={() => void resetToInherited()}
            >
              <RotateCcw /> Reset to inherited
            </Button>
          ) : null}
        </div>
      ) : null}
    </form>
  );
};
